import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation
import Loader from '../../common/Loader'; // Import Loader
import { AuthContext } from '../../../context/AuthContext';
import { fetchFinancialData, fetchWalletData } from '../../../api/apiService';
import InputField from '../../common/InputField';
import PaymentMethodScreen from './PaymentMethodScreen'; // Payment content + deposit processing (business logic preserved)

const MIN_AMOUNT_USD = 50;

const DepositScreen = () => {
    const { user } = useContext(AuthContext);
    const [accounts, setAccounts] = useState([]);
    const [wallets, setWallets] = useState([]);
    const [mergedOptions, setMergedOptions] = useState([]); // Combined accounts and wallets

    const [selectedOption, setSelectedOption] = useState('');
    const [amount, setAmount] = useState('');
    const [selectedPayment, setSelectedPayment] = useState('');

    const [transactionStatus, setTransactionStatus] = useState(null); // To track transaction status

    // Use useLocation to get query parameters
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const accountParam = queryParams.get('account'); // Get account ID from URL
    const walletParam = queryParams.get('wallet'); // Get wallet number from URL

    useEffect(() => {
        if (user) {
            const { access_token, user_id } = user;

            // Fetch financial data (accounts)
            fetchFinancialData(access_token, user_id).then((response) => {
                if (!response.error && response.data.trading_accounts) {
                    const realAccounts = response.data.trading_accounts.filter(
                        trading_accounts => trading_accounts.account_mode !== "Demo"
                    );
                    setAccounts(realAccounts);
                }
            });

            // Fetch wallet data
            fetchWalletData(access_token, user_id).then((response) => {
                if (!response.error && response.data.wallets) {
                    setWallets(response.data.wallets);
                }
            });
        }
        // location.search included to ensure fresh loads when sidebar changes query params
    }, [user, location.search]);

    // Merge accounts and wallets into a single array
    useEffect(() => {
        const combined = [
            ...accounts.map((account) => ({
                id: account.account_id,
                balance: account.balance,
                currency: 'USD', // Assuming accounts use USD as currency
                type: 'account',
            })),
            ...wallets.map((wallet) => ({
                id: wallet.wallet_number,
                balance: wallet.balance,
                currency: wallet.currency, // Use wallet currency
                type: 'wallet',
            })),
        ];
        setMergedOptions(combined);
    }, [accounts, wallets]);

    // Set the selected option if account or wallet is present in the URL
    useEffect(() => {
        // Reset step-specific UI so page loads "fresh" on every click
        setSelectedPayment('');
        setAmount('');
        setTransactionStatus(null);

        if (accountParam) {
            setSelectedOption(accountParam); // Set account ID as default
        } else if (walletParam) {
            setSelectedOption(walletParam); // Set wallet number as default
        } else {
            setSelectedOption('');
        }
    }, [accountParam, walletParam]);

    const selectedMeta = useMemo(() => {
        return mergedOptions.find((x) => String(x.id) === String(selectedOption));
    }, [mergedOptions, selectedOption]);

    const parsedAmount = parseFloat(amount);
    const hasAmount = amount !== '' && Number.isFinite(parsedAmount);

    const amountInlineError =
        hasAmount && parsedAmount < MIN_AMOUNT_USD
            ? `Minimum deposit amount is ${MIN_AMOUNT_USD} USD.`
            : '';

    const canContinue =
        Boolean(selectedOption) && hasAmount && parsedAmount >= MIN_AMOUNT_USD;

    // Continue button removed: deposit execution happens inside PaymentMethodScreen (e.g., PayPal onApprove).

    const handleTransactionComplete = (status) => {
        setTransactionStatus(status); // Set transaction status (success or failed)
    };

    return (
        <>
            <section className="p-5">
                <div className="w-full bg-white p-5 rounded-lg shadow-md border border-gray-100 relative">
                    <h2 className="text-xl font-bold mb-4 text-gray-800">Deposit Funds</h2>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
                        {/* Card 1 */}
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 flex flex-col">
                            <div className="mb-4">
                                <h3 className="text-sm font-semibold text-gray-800">Deposit Details</h3>
                            </div>

                            <div className="mb-4">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Account / Wallet <span className="text-red-500">*</span>
                                </label>
                                <select
                                    className="w-full h-10 px-3 border border-gray-200 rounded-lg focus:ring-1 focus:ring-gray-100 focus:outline-none"
                                    value={selectedOption}
                                    onChange={(e) => setSelectedOption(e.target.value)}
                                >
                                    <option value="">Select trading account or wallet</option>
                                    {mergedOptions.map((item) => (
                                        <option key={item.id} value={item.id}>
                                            {item.type === 'account' ? 'Trading Account' : 'Wallet'} - {item.id}
                                        </option>
                                    ))}
                                </select>

                                {/* Additional Information */}
                                {selectedMeta && (
                                    <div className="mt-3 rounded-lg bg-gray-50 border border-gray-100 p-3">
                                        <div className="text-xs text-gray-500">Available Balance</div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {selectedMeta.balance} {selectedMeta.currency}
                                        </div>
                                        <div className="mt-2 text-xs text-gray-500">Account Type</div>
                                        <div className="text-sm font-semibold text-gray-900">
                                            {selectedMeta.type === 'account' ? 'Trading Account' : 'Wallet'}
                                        </div>
                                        <div className="mt-2 text-xs text-gray-500">Currency</div>
                                        <div className="text-sm font-semibold text-gray-900">{selectedMeta.currency}</div>
                                    </div>
                                )}
                            </div>

                            <div className="mb-2">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Deposit Amount (USD) <span className="text-red-500">*</span>
                                </label>

                                <InputField
                                    type="number"
                                    className={[
                                        'w-full h-10 px-3 border rounded-lg focus:outline-none focus:ring-1',
                                        amountInlineError ? 'border-red-500 focus:ring-red-100' : 'border-gray-200 focus:ring-gray-100',
                                    ].join(' ')}
                                    placeholder="Enter amount"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                />

                                {/* Inline validation */}
                                {amountInlineError && (
                                    <div className="mt-2 text-sm text-red-600">
                                        {amountInlineError}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Card 2 */}
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm p-5 flex flex-col">
                            <div className="mb-4">
                                <h3 className="text-sm font-semibold text-gray-800">Payment Method</h3>
                            </div>

                            <div className="flex-1">
                                <PaymentMethodScreen
                                    user={user}
                                    selectedOption={selectedOption}
                                    amount={amount}
                                    selectedPayment={selectedPayment}
                                    onSelectPayment={setSelectedPayment}
                                    onTransactionComplete={handleTransactionComplete}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="hidden lg:block pointer-events-none absolute left-1/2 -translate-x-1/2 top-[210px]">
                        <div
                            className={[
                                'w-12 h-12 rounded-full border border-primary bg-white flex items-center justify-center shadow-sm',
                                canContinue ? 'animate-[pulse_900ms_ease-in-out_1]' : '',
                            ].join(' ')}
                        >
                            <span className="text-primary text-lg">→</span>
                        </div>
                    </div>


                    {/* Transaction Status Popup */}
                    {transactionStatus && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                            <div className="bg-white p-6 rounded-lg shadow-lg">
                                <h2 className="text-xl font-bold mb-4">
                                    {transactionStatus === 'success' ? 'Success!' : 'Failed!'}
                                </h2>
                                <p className="mb-4">
                                    {transactionStatus === 'success'
                                        ? 'Your deposit transaction was successful.'
                                        : 'Your deposit transaction failed. Please try again.'}
                                </p>
                                <button
                                    className="w-full bg-red-500 text-white p-2 rounded hover:bg-red-600 transition"
                                    onClick={() => setTransactionStatus(null)} // Close the popup
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default DepositScreen;
